/** @format */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AiFillStar } from "react-icons/ai";
import { BsFillGridFill } from "react-icons/bs";
import { CiStar } from "react-icons/ci";
import {
  FaChevronDown,
  FaFilter,
  FaSlidersH,
  FaThList,
  FaTimes,
} from "react-icons/fa";
import { Range } from "react-range";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import Footer from "../components/Footer";
import Headers from "../components/Headers";
import Paginations from "../components/Paginations";
import Products from "../components/products/Products";
import ShopProducts from "../components/products/ShopProducts";
import {
  get_products,
  price_range_products,
  query_products,
} from "../store/reducers/homeReducer";
import { useCategories } from "../utils/Categories";
import { Background, devise } from "../utils/utils";

// ─── Constants ────────────────────────────────────────────────────────────────

const STAR_LEVELS = [5, 4, 3, 2, 1];

// ─── StarRow ──────────────────────────────────────────────────────────────────

const StarRow = ({ filled }) => (
  <span className='flex gap-0.5 text-lg text-amber-400'>
    {[...Array(5)].map((_, i) =>
      i < filled ? (
        <AiFillStar key={i} />
      ) : (
        <CiStar key={i} className='text-slate-300' />
      ),
    )}
  </span>
);

// ─── ActiveChip ───────────────────────────────────────────────────────────────

const ActiveChip = ({ label, onRemove }) => (
  <span className='inline-flex items-center gap-1 pl-2.5 pr-1 py-0.5 rounded-full bg-blue-50 border border-blue-200 text-blue-700 text-xs font-medium'>
    {label}
    <button
      type='button'
      onClick={onRemove}
      className='ml-0.5 p-0.5 rounded-full hover:bg-blue-100 transition-colors'
      aria-label='Remove filter'
    >
      <FaTimes className='text-[9px]' />
    </button>
  </span>
);

// ─── SidebarSection ───────────────────────────────────────────────────────────

const SidebarSection = ({ title, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className='border-b border-slate-100 last:border-0'>
      <button
        type='button'
        onClick={() => setOpen((o) => !o)}
        className='w-full flex items-center justify-between py-4 text-left group'
      >
        <span className='text-sm font-semibold text-slate-700 tracking-wide uppercase'>
          {title}
        </span>
        <FaChevronDown
          className={`text-slate-400 text-xs transition-transform duration-200 group-hover:text-slate-600 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      <div
        className={`overflow-hidden transition-all duration-200 ${
          open ? "max-h-[500px] pb-4 opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        {children}
      </div>
    </div>
  );
};

// ─── ViewToggle ───────────────────────────────────────────────────────────────

const ViewToggle = ({ active, onClick, children }) => (
  <button
    type='button'
    onClick={onClick}
    className={`p-2 rounded-lg transition-all duration-150 ${
      active
        ? "bg-blue-600 text-white shadow-sm shadow-blue-200"
        : "bg-slate-100 text-slate-500 hover:bg-slate-200"
    }`}
  >
    {children}
  </button>
);

// ─── FilterSidebar ────────────────────────────────────────────────────────────

const FilterSidebar = ({
  t,
  priceRange,
  priceValues,
  onPriceChange,
  rating,
  onRatingChange,
  onRatingReset,
  priceRangeLatestProducts,
}) => (
  <div className='flex flex-col'>
    {/* Price Range */}
    <SidebarSection title={t("Filter by Price")}>
      <div className='flex flex-col gap-4 px-1'>
        <Range
          step={5}
          min={priceRange.min}
          max={priceRange.max}
          values={priceValues}
          onChange={onPriceChange}
          renderTrack={({ props, children }) => (
            <div
              {...props}
              className='relative w-full h-1.5 rounded-full bg-slate-200'
            >
              <div
                className='absolute h-full rounded-full bg-blue-500'
                style={{
                  left: `${
                    ((priceValues[0] - priceRange.min) /
                      (priceRange.max - priceRange.min)) *
                    100
                  }%`,
                  right: `${
                    100 -
                    ((priceValues[1] - priceRange.min) /
                      (priceRange.max - priceRange.min)) *
                      100
                  }%`,
                }}
              />
              {children}
            </div>
          )}
          renderThumb={({ props }) => (
            <div
              {...props}
              key={props.key}
              className='w-4 h-4 bg-white border-2 border-blue-500 rounded-full shadow-md focus:outline-none focus:ring-2 focus:ring-blue-300'
            />
          )}
        />
        <div className='flex items-center justify-between'>
          <span className='text-xs font-semibold text-slate-700 bg-slate-100 px-2.5 py-1 rounded-lg'>
            {Math.floor(priceValues[0])} {devise}
          </span>
          <span className='text-xs text-slate-400'>—</span>
          <span className='text-xs font-semibold text-slate-700 bg-slate-100 px-2.5 py-1 rounded-lg'>
            {Math.floor(priceValues[1])} {devise}
          </span>
        </div>
      </div>
    </SidebarSection>

    {/* Rating */}
    <SidebarSection title={t("Filter by Rating")}>
      <div className='flex flex-col gap-1'>
        {STAR_LEVELS.map((star) => (
          <button
            key={star}
            type='button'
            onClick={() => onRatingChange(star)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-left transition-all duration-100 ${
              rating === star
                ? "bg-amber-50 border border-amber-200"
                : "hover:bg-slate-50 border border-transparent"
            }`}
          >
            <StarRow filled={star} />
            <span className='text-xs text-slate-400'>& {t("up")}</span>
          </button>
        ))}
        {rating !== "" && (
          <button
            type='button'
            onClick={onRatingReset}
            className='flex items-center gap-1.5 mt-1 px-3 py-1.5 text-xs text-slate-500 hover:text-red-500 transition-colors rounded-lg hover:bg-red-50'
          >
            <FaTimes className='text-[10px]' />
            {t("Clear rating filter")}
          </button>
        )}
      </div>
    </SidebarSection>

    {/* Latest products widget */}
    <div className='pt-4 md:hidden'>
      <Products products={priceRangeLatestProducts} title={t("New Products")} />
    </div>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const SearchProduct = () => {
  const [searchParams] = useSearchParams();
  const categorie = searchParams.get("category");
  const searchValue = searchParams.get("value");

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [styles, setStyles] = useState("grid");
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [rating, setRating] = useState("");
  const [sortPrice, setSortPrice] = useState("");
  const [pageNumber, setPageNumber] = useState(1);

  const {
    products,
    totalProducts,
    priceRangeLatestProducts,
    priceRange,
    parPage,
  } = useSelector((state) => state.home);

  const [priceValues, setPriceValues] = useState([
    priceRange.min,
    priceRange.max,
  ]);

  const { categories, clientLocation } = useCategories();

  // Sync price range from Redux
  useEffect(() => {
    setPriceValues([
      priceRange.min === priceRange.max ? 0 : priceRange.min,
      priceRange.max,
    ]);
  }, [priceRange]);

  // Initial fetch
  useEffect(() => {
    if (!searchValue && !categorie) {
      dispatch(get_products());
    }
    dispatch(price_range_products());
  }, [dispatch, searchValue, categorie]);

  // Query on filter change
  useEffect(() => {
    dispatch(
      query_products({
        min: priceValues[0],
        max: priceValues[1],
        categorie,
        rating,
        sortPrice,
        pageNumber,
        searchValue,
      }),
    );
  }, [
    priceValues,
    categorie,
    dispatch,
    rating,
    pageNumber,
    sortPrice,
    searchValue,
  ]);

  // Handlers
  const handleRatingChange = useCallback((star) => {
    setRating(star);
    setPageNumber(1);
    setMobileFilterOpen(false);
  }, []);

  const handleRatingReset = useCallback(() => {
    setRating("");
    setPageNumber(1);
  }, []);

  const handlePriceChange = useCallback((values) => {
    setPriceValues(values);
    setPageNumber(1);
  }, []);

  const handleSortChange = useCallback((e) => {
    setSortPrice(e.target.value);
    setPageNumber(1);
  }, []);

  // Active filter chips
  const activeFilters = useMemo(() => {
    const chips = [];
    if (rating)
      chips.push({
        id: "rat",
        label: `${rating}+ stars`,
        clear: handleRatingReset,
      });
    return chips;
  }, [rating, handleRatingReset]);

  const showPagination = totalProducts > parPage;

  return (
    <div className='select-none bg-slate-50 min-h-screen'>
      <Headers categories={categories} clientLocation={clientLocation} />
      <Background t={t} />

      {/* Mobile Filter Drawer */}
      {mobileFilterOpen && (
        <div className='fixed inset-0 z-50 md-lg:flex hidden'>
          <div
            className='absolute inset-0 bg-black/40 backdrop-blur-sm'
            onClick={() => setMobileFilterOpen(false)}
          />
          <div className='relative ml-auto w-[85%] max-w-sm h-full bg-white shadow-2xl flex flex-col overflow-hidden'>
            <div className='flex items-center justify-between px-5 py-4 border-b border-slate-100'>
              <div className='flex items-center gap-2'>
                <FaSlidersH className='text-blue-600' />
                <span className='font-semibold text-slate-800'>
                  {t("Filters")}
                </span>
              </div>
              <button
                type='button'
                onClick={() => setMobileFilterOpen(false)}
                className='p-1.5 rounded-lg hover:bg-slate-100 transition-colors'
              >
                <FaTimes className='text-slate-500' />
              </button>
            </div>
            <div className='flex-1 overflow-y-auto px-5 py-2'>
              <FilterSidebar
                t={t}
                priceRange={priceRange}
                priceValues={priceValues}
                onPriceChange={handlePriceChange}
                rating={rating}
                onRatingChange={handleRatingChange}
                onRatingReset={handleRatingReset}
                priceRangeLatestProducts={priceRangeLatestProducts}
              />
            </div>
          </div>
        </div>
      )}

      {/* Main layout */}
      <section className='py-10 sm:py-6'>
        <div className='w-[95%] lg:w-[90%] mx-auto'>
          <div className='flex gap-8 md:flex-col'>
            {/* Sidebar – desktop */}
            <aside className='w-64 flex-shrink-0 md:hidden'>
              <div className='bg-white rounded-xl border border-slate-100 shadow-sm px-5 py-2 sticky top-4'>
                <div className='flex items-center gap-2 py-4 border-b border-slate-100 mb-1'>
                  <FaSlidersH className='text-blue-600 text-sm' />
                  <span className='text-sm font-bold text-slate-800 tracking-wide uppercase'>
                    {t("Filters")}
                  </span>
                </div>
                <FilterSidebar
                  t={t}
                  priceRange={priceRange}
                  priceValues={priceValues}
                  onPriceChange={handlePriceChange}
                  rating={rating}
                  onRatingChange={handleRatingChange}
                  onRatingReset={handleRatingReset}
                  priceRangeLatestProducts={priceRangeLatestProducts}
                />
              </div>
            </aside>

            {/* Products area */}
            <div className='flex-1 min-w-0'>
              {/* Toolbar */}
              <div className='bg-white rounded-xl border border-slate-100 shadow-sm px-4 py-3 mb-6 flex items-center justify-between gap-4 flex-wrap'>
                {/* Left */}
                <div className='flex items-center gap-3 flex-wrap'>
                  {/* Mobile filter button */}
                  <button
                    type='button'
                    onClick={() => setMobileFilterOpen(true)}
                    className='hidden md:flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700 transition-colors'
                  >
                    <FaFilter className='text-[10px]' />
                    {t("Filters")}
                    {activeFilters.length > 0 && (
                      <span className='ml-1 bg-white text-blue-700 rounded-full w-4 h-4 flex items-center justify-center text-[10px] font-bold'>
                        {activeFilters.length}
                      </span>
                    )}
                  </button>

                  <div>
                    <span className='text-sm font-bold text-slate-800'>
                      {totalProducts}
                    </span>
                    <span className='text-sm text-slate-400 ml-1'>
                      {t("Products")}
                    </span>
                  </div>

                  {/* Active chips */}
                  {activeFilters.length > 0 && (
                    <div className='flex items-center gap-1.5 flex-wrap'>
                      {activeFilters.map((chip) => (
                        <ActiveChip
                          key={chip.id}
                          label={chip.label}
                          onRemove={chip.clear}
                        />
                      ))}
                    </div>
                  )}
                </div>

                {/* Right */}
                <div className='flex items-center gap-3'>
                  <select
                    onChange={handleSortChange}
                    value={sortPrice}
                    className='text-xs font-semibold text-slate-700 border border-slate-200 rounded-lg px-3 py-1.5 bg-white outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition-all cursor-pointer'
                  >
                    <option value=''>{t("Sort by")}</option>
                    <option value='low-to-high'>{t("Lowest Price")}</option>
                    <option value='high-to-low'>{t("Highest Price")}</option>
                  </select>

                  <div className='flex items-center gap-1.5 md-lg:hidden'>
                    <ViewToggle
                      active={styles === "grid"}
                      onClick={() => setStyles("grid")}
                    >
                      <BsFillGridFill className='text-sm' />
                    </ViewToggle>
                    <ViewToggle
                      active={styles === "list"}
                      onClick={() => setStyles("list")}
                    >
                      <FaThList className='text-sm' />
                    </ViewToggle>
                  </div>
                </div>
              </div>

              {/* Products */}
              <div className='pb-8'>
                <ShopProducts products={products} styles={styles} grid={5} />
              </div>

              {/* Pagination */}
              {showPagination && (
                <div className='flex justify-center pt-2 pb-8'>
                  <Paginations
                    pageNumber={pageNumber}
                    setPageNumber={setPageNumber}
                    totalItem={totalProducts}
                    parPage={parPage}
                    showItem={Math.floor(totalProducts / parPage) + 1}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SearchProduct;
